import { useState, useMemo } from 'react'
import { StudentShell } from '@/components/shared/StudentShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Calendar, CheckCircle2, Search, Users, HelpCircle,
  FileCheck, UserPlus, Compass, Filter, Award, ArrowRight,
  BookCheck, GraduationCap, X, ChevronRight, Layers
} from 'lucide-react'

interface ExamItem {
  id: number
  name: string
  code: string
  year: number | null
  enrolled: boolean
  tests_count: number
  questions_count: number
  enrolled_students_count: number
  completed_attempts_count: number
}

interface Props {
  exams: ExamItem[]
}

type TabFilter = 'all' | 'enrolled' | 'available'

export default function StudentExamsIndex({ exams = [] }: Props) {
  const [search, setSearch] = useState('')
  const [selectedYear, setSelectedYear] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<TabFilter>('all')

  const enrolledExams = useMemo(() => exams.filter((e) => e.enrolled), [exams])
  const availableExams = useMemo(() => exams.filter((e) => !e.enrolled), [exams])

  // Extract available years for filtering
  const availableYears = useMemo(() => {
    return Array.from(new Set(exams.map((e) => e.year).filter(Boolean))) as number[]
  }, [exams])

  // Filter available exams if search or year is set
  const filteredAvailable = useMemo(() => {
    return availableExams.filter((exam) => {
      const matchesSearch =
        exam.name.toLowerCase().includes(search.toLowerCase()) ||
        exam.code.toLowerCase().includes(search.toLowerCase())
      const matchesYear =
        selectedYear === 'all' || (exam.year && String(exam.year) === selectedYear)
      return matchesSearch && matchesYear
    })
  }, [availableExams, search, selectedYear])

  const filteredEnrolled = useMemo(() => {
    return enrolledExams.filter((exam) => {
      const matchesSearch =
        exam.name.toLowerCase().includes(search.toLowerCase()) ||
        exam.code.toLowerCase().includes(search.toLowerCase())
      const matchesYear =
        selectedYear === 'all' || (exam.year && String(exam.year) === selectedYear)
      return matchesSearch && matchesYear
    })
  }, [enrolledExams, search, selectedYear])

  const handleEnroll = (examId: number) => {
    router.post(`/student/exams/${examId}/enroll`, {}, { preserveScroll: true })
  }

  const renderExamCard = (exam: ExamItem) => (
    <Card
      key={exam.id}
      className={`border bg-card hover:border-primary/50 transition-all duration-300 hover:shadow-glass-md rounded-2xl flex flex-col justify-between group overflow-hidden relative ${
        exam.enrolled ? 'border-primary/30' : 'border-border/80'
      }`}
    >
      <CardContent className="p-5 sm:p-6 space-y-4 flex flex-col justify-between flex-1">
        {/* Top Badges Row */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <Badge variant="outline" className="font-mono text-[10px] font-bold border-border/80 text-muted-foreground px-2 py-0.5 rounded-md">
              {exam.code}
            </Badge>
            {exam.enrolled ? (
              <Badge className="font-extrabold text-[11px] bg-primary/15 text-primary border border-primary/30 rounded-lg px-2.5 py-0.5 flex items-center gap-1 shadow-2xs">
                <CheckCircle2 className="h-3 w-3" />
                <span>Enrolled</span>
              </Badge>
            ) : (
              <Badge variant="outline" className="font-extrabold text-[11px] flex items-center gap-1 bg-tertiary/15 text-tertiary border-tertiary/35 rounded-lg px-2.5 py-0.5 shadow-2xs">
                <Compass className="h-3 w-3 text-tertiary" />
                <span>Available</span>
              </Badge>
            )}
          </div>

          {/* Title & Year */}
          <div className="space-y-1">
            <h3 className="text-base sm:text-lg font-bold text-foreground group-hover:text-primary transition-colors leading-snug line-clamp-2">
              {exam.name}
            </h3>
            {exam.year ? (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <Calendar className="h-3.5 w-3.5 text-primary" />
                <span>Exam Year: {exam.year}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                <GraduationCap className="h-3.5 w-3.5 text-primary" />
                <span>Target Entrance Exam</span>
              </div>
            )}
          </div>
        </div>

        {/* Metadata & Actions */}
        <div className="space-y-4 pt-2 border-t border-border/40">
          {/* Exam Stats Matrix (4 metrics) */}
          <div className="grid grid-cols-4 gap-1.5 bg-muted/40 p-2.5 rounded-xl border border-border/40 text-center">
            <div className="flex flex-col items-center justify-center">
              <span className="text-[9px] font-bold text-muted-foreground uppercase flex items-center gap-1 truncate">
                <FileCheck className="h-3 w-3 text-primary shrink-0" /> Tests
              </span>
              <span className="text-xs font-extrabold text-foreground mt-0.5">{exam.tests_count}</span>
            </div>

            <div className="flex flex-col items-center justify-center border-l border-border/40">
              <span className="text-[9px] font-bold text-muted-foreground uppercase flex items-center gap-1 truncate">
                <HelpCircle className="h-3 w-3 text-secondary shrink-0" /> Qs
              </span>
              <span className="text-xs font-extrabold text-foreground mt-0.5">{exam.questions_count}</span>
            </div>

            <div className="flex flex-col items-center justify-center border-l border-border/40">
              <span className="text-[9px] font-bold text-muted-foreground uppercase flex items-center gap-1 truncate">
                <Users className="h-3 w-3 text-tertiary shrink-0" /> Students
              </span>
              <span className="text-xs font-extrabold text-foreground mt-0.5">{exam.enrolled_students_count}</span>
            </div>

            <div className="flex flex-col items-center justify-center border-l border-border/40">
              <span className="text-[9px] font-bold text-muted-foreground uppercase flex items-center gap-1 truncate">
                <Award className="h-3 w-3 text-primary shrink-0" /> Attempts
              </span>
              <span className="text-xs font-extrabold text-foreground mt-0.5">{exam.completed_attempts_count}</span>
            </div>
          </div>

          {/* Action CTA */}
          {exam.enrolled ? (
            <Button
              variant="outline"
              className="w-full h-10 rounded-xl font-bold text-xs sm:text-sm border-primary/40 text-primary hover:bg-primary/10 transition-all shadow-glass-xs cursor-pointer"
              asChild
            >
              <Link href={`/student/exams/${exam.id}`}>
                <span>View Tests</span>
                <ArrowRight className="ml-1.5 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full h-10 rounded-xl font-bold text-xs sm:text-sm border-border hover:border-primary/50 text-foreground hover:bg-primary/10 transition-all shadow-glass-xs cursor-pointer"
              onClick={() => handleEnroll(exam.id)}
            >
              <UserPlus className="mr-1.5 h-4 w-4 text-tertiary" />
              <span>Enroll Now (Free)</span>
              <ChevronRight className="ml-auto h-4 w-4 opacity-70 group-hover:translate-x-0.5 transition-transform" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )

  return (
    <StudentShell>
      {/* ─── MOBILE VIEW ONLY (< lg) ─── */}
      <div className="block lg:hidden space-y-4 pb-6">

        {/* Hero Header */}
        <div className="space-y-1 pt-1">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] font-bold uppercase tracking-widest">
            <Compass className="h-3 w-3" />
            Target Entrance Exams
          </span>
          <h1 className="text-xl font-extrabold text-foreground tracking-tight">Target Competitive Exams</h1>
          <p className="text-xs text-muted-foreground">Enrol in your target entrance exams to access tailored mock tests, practice questions, and leaderboard rankings.</p>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Enrolled', value: enrolledExams.length, color: 'text-primary bg-primary/10 border-primary/20' },
            { label: 'Available', value: availableExams.length, color: 'text-secondary bg-secondary/10 border-secondary/20' },
            { label: 'Total', value: exams.length, color: 'text-foreground bg-muted/50 border-border' },
          ].map(stat => (
            <div key={stat.label} className={`flex flex-col items-center py-2.5 rounded-2xl border font-bold ${stat.color}`}>
              <span className="text-xl font-extrabold">{stat.value}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider mt-0.5 opacity-80">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Tab Filter Pill Row */}
        <div className="flex items-center gap-1.5 bg-muted/40 p-1 rounded-2xl border border-border/50">
          {(['all', 'enrolled', 'available'] as const).map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                activeTab === tab
                  ? 'bg-card text-foreground font-bold shadow-2xs border border-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'all' ? `All Exams ${exams.length}` 
               : tab === 'enrolled' ? `Enrolled ${enrolledExams.length}`
               : `Available ${availableExams.length}`}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search exam..."
            className="pl-9 pr-9 rounded-2xl border-border bg-card h-10 text-sm"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Enrolled Exams List */}
        {(activeTab === 'all' || activeTab === 'enrolled') && enrolledExams.length > 0 && (
          <div className="space-y-2.5">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">My Enrolled Exams ({enrolledExams.length})</h2>
            {enrolledExams
              .filter(e =>
                search === '' ||
                e.name.toLowerCase().includes(search.toLowerCase()) ||
                e.code.toLowerCase().includes(search.toLowerCase())
              )
              .map(exam => (
              <div key={exam.id} className="p-4 rounded-2xl border border-border bg-card shadow-2xs space-y-3">
                {/* Card Header */}
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-0.5 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{exam.code}</span>
                      <span className="text-[10px] font-bold text-secondary bg-secondary/10 px-2 py-0.5 rounded-full border border-secondary/20">Enrolled</span>
                    </div>
                    <p className="text-sm font-extrabold text-foreground truncate">{exam.name}</p>
                    {exam.year && <p className="text-[11px] text-muted-foreground">Exam Year: {exam.year}</p>}
                  </div>
                </div>
                {/* Stats Chips */}
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { label: 'TESTS', value: exam.tests_count },
                    { label: 'QS', value: exam.questions_count },
                    { label: 'STUDENTS', value: exam.enrolled_students_count },
                    { label: 'ATTEMPTS', value: exam.completed_attempts_count },
                  ].map(stat => (
                    <div key={stat.label} className="flex flex-col items-center py-1.5 bg-muted/40 rounded-xl border border-border/50">
                      <span className="text-sm font-bold text-foreground">{stat.value}</span>
                      <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide mt-0.5">{stat.label}</span>
                    </div>
                  ))}
                </div>
                {/* Action Row */}
                <div>
                  <Button asChild className="w-full bg-primary text-primary-fg font-bold rounded-xl text-xs h-9 gap-1">
                    <Link href={`/student/exams/${exam.id}`}>
                      View Tests <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Available Exams List */}
        {(activeTab === 'all' || activeTab === 'available') && filteredAvailable.length > 0 && (
          <div className="space-y-2.5">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Explore Available Exams ({filteredAvailable.length})</h2>
            {filteredAvailable.map(exam => (
              <div key={exam.id} className="p-4 rounded-2xl border border-border bg-card shadow-2xs space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-0.5 min-w-0">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{exam.code}</span>
                    <p className="text-sm font-extrabold text-foreground truncate">{exam.name}</p>
                    {exam.year && <p className="text-[11px] text-muted-foreground">Exam Year: {exam.year}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { label: 'TESTS', value: exam.tests_count },
                    { label: 'QS', value: exam.questions_count },
                    { label: 'STUDENTS', value: exam.enrolled_students_count },
                    { label: 'ATTEMPTS', value: exam.completed_attempts_count },
                  ].map(stat => (
                    <div key={stat.label} className="flex flex-col items-center py-1.5 bg-muted/40 rounded-xl border border-border/50">
                      <span className="text-sm font-bold text-foreground">{stat.value}</span>
                      <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide mt-0.5">{stat.label}</span>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => handleEnroll(exam.id)}
                  className="w-full bg-primary text-primary-fg font-bold rounded-xl text-xs h-9 gap-1"
                >
                  <UserPlus className="h-3.5 w-3.5" /> Enroll Now
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Empty States */}
        {activeTab === 'enrolled' && enrolledExams.length === 0 && (
          <div className="py-12 flex flex-col items-center text-center space-y-3">
            <Compass className="h-10 w-10 text-muted-foreground/30" />
            <p className="text-sm font-bold text-foreground">No Enrolled Exams</p>
            <p className="text-xs text-muted-foreground">Enroll in an exam to access mock tests and rankings.</p>
            <Button
              variant="outline"
              className="rounded-2xl border-primary/30 text-primary hover:bg-primary/10 font-bold text-xs h-9 mt-2"
              onClick={() => setActiveTab('available')}
            >
              Explore Exams
            </Button>
          </div>
        )}
        {activeTab === 'available' && filteredAvailable.length === 0 && (
          <div className="py-12 flex flex-col items-center text-center space-y-3">
            <CheckCircle2 className="h-10 w-10 text-secondary/40" />
            <p className="text-sm font-bold text-foreground">Enrolled in All Exams</p>
            <p className="text-xs text-muted-foreground">You are enrolled in all active exams with published tests.</p>
          </div>
        )}
      </div>

      {/* ─── DESKTOP VIEW ONLY (>= lg) ─── */}
      <div className="hidden lg:block space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-16">
        {/* Hero Card Banner */}
        <div className="rounded-2xl border border-border/70 bg-card p-5 sm:p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-2.5 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-0.5 rounded-full border border-primary/25 inline-flex items-center gap-1.5 shadow-2xs">
                  <GraduationCap className="h-3 w-3 text-primary" />
                  <span>Target Entrance Exams</span>
                </span>
              </div>

              <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
                Target Competitive Exams
              </h1>

              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Enroll in your target entrance exams to access tailored mock tests, practice questions, and leaderboard rankings.
              </p>
            </div>

            {/* Quick Metrics Strip — Vibrant Semantic Token Cards */}
            <div className="grid grid-cols-3 gap-2.5 sm:gap-3.5 shrink-0 lg:max-w-md w-full">
              <div className="flex flex-col justify-center p-3.5 sm:p-4 rounded-xl bg-secondary/10 border border-secondary/25 transition-all hover:bg-secondary/15">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-secondary shrink-0" />
                  <span className="text-[10px] font-bold text-secondary uppercase tracking-wider">Enrolled</span>
                </div>
                <span className="text-xl sm:text-2xl font-extrabold text-secondary mt-1">{enrolledExams.length}</span>
              </div>

              <div className="flex flex-col justify-center p-3.5 sm:p-4 rounded-xl bg-primary/10 border border-primary/25 transition-all hover:bg-primary/15">
                <div className="flex items-center gap-1.5">
                  <Compass className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Available</span>
                </div>
                <span className="text-xl sm:text-2xl font-extrabold text-primary mt-1">{availableExams.length}</span>
              </div>

              <div className="flex flex-col justify-center p-3.5 sm:p-4 rounded-xl bg-tertiary/10 border border-tertiary/25 transition-all hover:bg-tertiary/15">
                <div className="flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-tertiary shrink-0" />
                  <span className="text-[10px] font-bold text-tertiary uppercase tracking-wider">Total</span>
                </div>
                <span className="text-xl sm:text-2xl font-extrabold text-tertiary mt-1">{exams.length}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
          {/* Tabs */}
          <div className="flex items-center p-1 bg-muted/50 rounded-2xl border border-border/60 overflow-x-auto shrink-0 shadow-2xs">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                activeTab === 'all'
                  ? 'bg-card text-foreground shadow-xs border border-border/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span>All Exams</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-extrabold ${activeTab === 'all' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                {exams.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('enrolled')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                activeTab === 'enrolled'
                  ? 'bg-card text-foreground shadow-xs border border-border/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
              <span>Enrolled</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-extrabold ${activeTab === 'enrolled' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                {enrolledExams.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('available')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 ${
                activeTab === 'available'
                  ? 'bg-card text-foreground shadow-xs border border-border/40'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Compass className="h-3.5 w-3.5 text-tertiary" />
              <span>Available</span>
              <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-extrabold ${activeTab === 'available' ? 'bg-tertiary/15 text-tertiary' : 'bg-muted text-muted-foreground'}`}>
                {availableExams.length}
              </span>
            </button>
          </div>

          {/* Search and Year filter */}
          <div className="flex items-center gap-2 flex-1 sm:max-w-md justify-end">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder="Search exam by name or code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-8 h-10 rounded-xl bg-card border-border/70 text-xs sm:text-sm focus-visible:ring-primary"
              />
              {search && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearch('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground rounded-lg"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            {availableYears.length > 0 && (
              <div className="shrink-0 w-36">
                <Select value={selectedYear} onValueChange={(val) => setSelectedYear(val)}>
                  <SelectTrigger className="h-10 rounded-xl bg-card border-border/70 text-xs font-semibold text-foreground focus:ring-primary shadow-2xs">
                    <div className="flex items-center gap-1.5 truncate">
                      <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <SelectValue placeholder="All Years" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50">
                    <SelectGroup>
                      <SelectItem value="all" className="text-xs font-medium cursor-pointer">
                        All Years
                      </SelectItem>
                      {availableYears.map((yr) => (
                        <SelectItem key={yr} value={String(yr)} className="text-xs font-medium cursor-pointer">
                          {yr}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* SECTION 1: Enrolled Exams */}
        {(activeTab === 'all' || activeTab === 'enrolled') && (
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-3">
              <div className="flex items-center gap-2">
                <BookCheck className="h-4.5 w-4.5 text-primary shrink-0" />
                <h2 className="text-base sm:text-lg font-bold text-foreground">
                  My Enrolled Exams ({filteredEnrolled.length})
                </h2>
              </div>
              <span className="hidden sm:inline text-xs text-muted-foreground">
                Mock tests for enrolled exams appear on your Dashboard
              </span>
            </div>

            {filteredEnrolled.length === 0 ? (
              <Card className="p-6 sm:p-10 text-center border-dashed border-border/80 bg-card/40 rounded-2xl">
                <div className="w-12 h-12 rounded-2xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto mb-3">
                  <Users className="h-6 w-6" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-foreground">No Enrolled Exams</h3>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                  {search
                    ? 'No enrolled exams match your search query.'
                    : 'Explore the available entrance exams below and click "Enroll Now" to get started!'}
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {filteredEnrolled.map((exam) => renderExamCard(exam))}
              </div>
            )}
          </section>
        )}

        {/* SECTION 2: Available Exams */}
        {(activeTab === 'all' || activeTab === 'available') && (
          <section className="space-y-4 pt-2">
            <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-3">
              <div className="flex items-center gap-2">
                <Compass className="h-4.5 w-4.5 text-tertiary shrink-0" />
                <h2 className="text-base sm:text-lg font-bold text-foreground">
                  Explore Available Exams ({filteredAvailable.length})
                </h2>
              </div>
              <span className="hidden sm:inline text-xs text-muted-foreground">
                Select target exams to access question banks and mock tests
              </span>
            </div>

            {filteredAvailable.length === 0 ? (
              <Card className="p-6 sm:p-10 text-center border-dashed border-border/80 bg-card/40 rounded-2xl">
                <div className="w-12 h-12 rounded-2xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-foreground">
                  {availableExams.length === 0 ? 'Enrolled in All Exams' : 'No Matching Exams'}
                </h3>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                  {availableExams.length === 0
                    ? 'You are enrolled in all active exams with published tests.'
                    : 'No available exams match your search filters.'}
                </p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {filteredAvailable.map((exam) => renderExamCard(exam))}
              </div>
            )}
          </section>
        )}
      </div>
    </StudentShell>
  )
}
